import { defineStore } from "pinia";
import { useWindowSize } from "@vueuse/core";
// import { useRoute } from "vue-router";
import { useToast } from "primevue/usetoast";
import {
	getLocalStorageReac,
	setLocalStorageReac,
	simpleCrypt,
	simpleDecrypt,
} from "@/assets/js/helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { $t } from "@/i18n";
import type {
	IStatus,
	IUserStatus,
	IRoles,
	IPermissions,
	TCategory,
	TGeoscope,
	TEntity,
	TUser,
} from "@/api/types";
import api from "@/api";
import type { TPlayer } from "@/assets/js/globalTypes";
import { APP_NAME } from "@/assets/js/appConfig";
dayjs.extend(utc);
dayjs.extend(timezone);

// const tagsList = ref<ITag[] | null>(null);
const categoryList = ref<TCategory[] | null>(null);
const createCategoryVisible = ref(false);
const newCategoryParent = ref<TCategory>(null);
const geoscopeList = ref<TGeoscope[] | null>(null);
const createGeoscopeVisible = ref(false);
const entityList = ref<TEntity[] | null>(null);
const editEntityVisible = ref(false);
const selectedEntity = ref<TEntity>();
const createEntityVisible = ref(false);
const userData = ref<TUser | null>(null);
const isWsConnecting = ref(false);
const isVisibleCreateRoomDialog = ref(false);
const isAppLoading = ref(true);
const isAppReady = ref(false);
const gameToken = ref<string | null>("");
const errorMessage = ref("");
const errorToastTrigger = ref(false);
const multiplayerGameActive = ref(false);
type TMultiplayerGamePlayer = {
	is_me: boolean;
	is_on_turn?: boolean;
	mpg_usr_color_hex: string | null;
	mpg_usr_has_left_game?: 1 | 0;
	mpg_usr_is_eliminated?: 1 | 0;
	mpg_usr_is_creator: 1 | 0;
	mpg_usr_order?: number;
	usr_username: string | null;
};
type TMultiplayerGameMovie = {
	artist?: string | null;
	artist_credit?: string | null;
	backdrop_path: string | null;
	birth_date?: string | null;
	cover_thumb_250_url?: string | null;
	cover_thumb_500_url?: string | null;
	cover_url?: string | null;
	id: number;
	image_url?: string | null;
	media_type?: "movie" | "music" | "person";
	name?: string | null;
	original_title?: string | null;
	photo_url?: string | null;
	poster_path: string | null;
	profile_image_url?: string | null;
	profile_path?: string | null;
	title: string;
};
type TMultiplayerGameResultPlayer = TMultiplayerGamePlayer & {
	is_winner: boolean;
	mpg_usr_has_left_game: 1 | 0;
	mpg_usr_is_eliminated: 1 | 0;
	mpg_usr_order: number;
	rank: number;
	reached_round: number;
};
const multiplayerGamePlayerList = ref<TMultiplayerGamePlayer[]>([]);
const multiplayerGameMovies = ref<TMultiplayerGameMovie[]>([]);
const multiplayerGameSecondsLeft = ref<number | null>(null);
const multiplayerGameIsActive = ref(false);
const multiplayerGameIsCorrect = ref<boolean | null>(null);
const multiplayerGameRound = ref(1);
const multiplayerGameIsOver = ref(false);
const multiplayerGameResults = ref<TMultiplayerGameResultPlayer[]>([]);

const playerToken = ref<string>("");
const mmgToken = ref<string | null>("");
const isVisibleJoinDialog = ref(false);
const isRoomProtected = ref(false);
const gamePlayers = ref<TPlayer[]>([]);
const isVisibleLobbyModal = ref(false);
const getReadyState = ref(false);
const isWrongPassword = ref(false);
const isLoadingJoinGame = ref(false);

let resolveAppReady: (() => void) | null = null;
const appReadyPromise = new Promise<void>((resolve) => {
	resolveAppReady = resolve;
});

function markAppReady() {
	if (isAppReady.value) {
		return;
	}

	isAppReady.value = true;
	resolveAppReady?.();
	resolveAppReady = null;
}

function waitForAppReady() {
	if (isAppReady.value) {
		return Promise.resolve();
	}

	return appReadyPromise;
}

// const MAIN_CATEGORIES = {
// 	MOVIES: "MOVI",
// 	MUSIC: "MUSC",
// };

const MAIN_CATEGORIES = {
	MOVIES: "MOVI",
	MUSIC: "MUSC",
	PEOPLE: "PEOP",
} as const;

export type TMainCategory =
	(typeof MAIN_CATEGORIES)[keyof typeof MAIN_CATEGORIES];

const multiplayerGameCategory = ref<TMainCategory>(MAIN_CATEGORIES.MOVIES);

const SINGLE_PLAYER_GAME_TYPES = {
	CLASSIC_GAME: "CLS",
	BEAT_THE_CLOCK: "BTC",
} as const;

export type TSinglePlayerGameType =
	(typeof SINGLE_PLAYER_GAME_TYPES)[keyof typeof SINGLE_PLAYER_GAME_TYPES];

const MULTI_PLAYER_GAME_TYPES = {
	WEAKEST_LINK: "WKL",
} as const;

export type TMultiPlayerGameType =
	(typeof MULTI_PLAYER_GAME_TYPES)[keyof typeof MULTI_PLAYER_GAME_TYPES];

// const CONST = {
//   DEFAULT_LANGUAGE: 'en-US',
//   SINGLE_PLAYER_GAME_TYPES: {
//     CLASSIC_GAME: 'CLS',
//     BEAT_THE_CLOCK: 'BTC'
//   },
//   MULTI_PLAYER_GAME_TYPES: {
//     WEAKEST_LINK: 'weakest-link'
//   },
//   BTC_DURATION_SEC: 60,
//   VALID_ANSWERS: {
//     HIGHER: 'higher',
//     LOWER: 'lower'
//   },
//   CATEGORIES: {
//     MOVIES: 'MOVI',
//     MUSIC: 'MUSC'
//   }
// }

function formatDatepickerDate(date: string): string {
	const localDate = dayjs(date);
	const utcDate = localDate.utc();
	return utcDate.format("YYYY-MM-DD HH:mm:ss");
}

function utcToLocale(date: string, withTime: boolean): string {
	const res = dayjs.utc(date).local(); // temp solution !!!!!!!
	// console.log(res.format("YYYY-MM-DD HH:mm:ss"));

	return withTime
		? res.format("MMM DD, YYYY HH:mm")
		: res.format("MMM DD, YYYY");
}

function getFirstLetter(str: string): string {
	if (typeof str !== "string" || str.trim() === "") {
		return "";
	}
	return str.trim().charAt(0);
}

async function getCategories(params?: {
	search?: string;
	cat_id_parent?: number;
}) {
	try {
		const res = await api.categoriesGet(params);
		categoryList.value = res.data.data;
		// console.log(tagsList.value);
	} catch (err: any) {
		if (err.response.data.message.includes("Invalid param search")) {
			useToast().add({
				severity: "error",
				summary: $t("app.postForm.error.invalidSearch.title"),
				detail: $t("app.postForm.error.invalidSearch.msg"),
				life: 3000,
			});
		}
		// console.log(err.response.data.message);
		console.warn(err.message);
	}
}

async function getGeoscopes(params?: { search?: string }) {
	try {
		const res = await api.geoscopesGet(params);
		geoscopeList.value = res.data.data;
		// console.log(tagsList.value);
	} catch (err: any) {
		if (err.response.data.message.includes("Invalid param search")) {
			useToast().add({
				severity: "error",
				summary: $t("app.postForm.error.invalidSearch.title"),
				detail: $t("app.postForm.error.invalidSearch.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	}
}

async function getEntities(params?: { search?: string; ent_id?: number }) {
	try {
		const res = await api.entitiesGet(params);
		entityList.value = res.data.data;
		// console.log(tagsList.value);
	} catch (err: any) {
		if (err.response.data.message.includes("Invalid param search")) {
			useToast().add({
				severity: "error",
				summary: $t("app.postForm.error.invalidSearch.title"),
				detail: $t("app.postForm.error.invalidSearch.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	}
}

const USER_TOKEN_KEY = "user-token";

type TGetUserResponse = {
	ok: boolean;
	isNew?: boolean;
	user?: {
		usr_id: number;
		usr_token: string;
		usr_username?: string;
		usr_movies_classic_best_score: number | null;
		usr_movies_classic_last_score: number | null;
		usr_movies_btc_best_score: number | null;
		usr_movies_btc_last_score: number | null;
		usr_people_classic_best_score?: number | null;
		usr_people_classic_last_score?: number | null;
		usr_people_btc_best_score?: number | null;
		usr_people_btc_last_score?: number | null;
		usr_people_filter_preset_code?: string | null;
		usr_people_only_globally_known?: boolean | 1 | 0 | null;
		usr_people_show_adult_names?: boolean | 1 | 0 | null;
		usr_should_show_whats_new?: boolean | 1 | 0 | null;
		usr_utc_created: string;
	};
};

async function getUser() {
	const storedTokenRef = getLocalStorageReac<string | null>(
		USER_TOKEN_KEY,
		false,
		null,
	);

	const token =
		storedTokenRef.value && storedTokenRef.value !== "null"
			? storedTokenRef.value
			: null;

	try {
		const res = await api.getUser({ token });
		const data: TGetUserResponse = res.data.res;

		if (data?.ok) {
			if (data?.user?.usr_token) {
				setLocalStorageReac(USER_TOKEN_KEY, data.user.usr_token);
				playerToken.value = data.user.usr_token;
			}
			if (data?.user?.usr_username) {
				setLocalStorageReac("username", data.user.usr_username);
			}
		}

		// todo greska ako nema usera
		userData.value = data.user ?? null;
	} catch (err: any) {
		console.warn(err.message);
		return null;
	}
}

export const useDefaultStore = defineStore("main", () => {
	const { width } = useWindowSize();
	const isMobileToggled = ref(false);
	// const router = useRouter();
	// const route = useRoute();

	const statuses = ref<IStatus[]>([
		{
			name: $t("app.postForm.statuses.published"),
			code: "PUB",
			permission_create: "publish-post",
		},
		{ name: $t("app.postForm.statuses.draft"), code: "DRA" },
		{
			name: $t("app.postForm.statuses.hidden"),
			code: "HID",
			permission_create: "hide-post",
			permission_view: "view-hidden",
		},
	]);

	const userStatuses = ref<IUserStatus[]>([
		{
			value: "active",
			label: $t("app.manageUsers.statuses.active"),
		},
		{ value: "banned", label: $t("app.manageUsers.statuses.banned") },
	]);

	const roles = ref<IRoles[]>([
		{
			rol_id: 999,
			rol_name: "super-admin",
			rol_label: "Super Admin",
		},
		{
			rol_id: 700,
			rol_name: "contributor",
			rol_label: "Contributor",
		},
		{
			rol_id: 800,
			rol_name: "editor",
			rol_label: "Editor",
		},
	]);

	const permissions = ref<IPermissions[]>([
		{
			per_id: 1,
			per_name: "create-post",
			per_label: "Create Post",
		},
		{
			per_id: 2,
			per_name: "edit-post",
			per_label: "Edit Post",
		},
		{
			per_id: 3,
			per_name: "delete-post",
			per_label: "Delete Post",
		},
		{
			per_id: 4,
			per_name: "publish-post",
			per_label: "Publish Post",
		},
		{
			per_id: 5,
			per_name: "view-hidden",
			per_label: "View Hidden",
		},
		{
			per_id: 6,
			per_name: "view-all-posts",
			per_label: "View All Posts",
		},
		{
			per_id: 7,
			per_name: "hide-post",
			per_label: "Hide Post",
		},
		{
			per_id: 8,
			per_name: "create-user",
			per_label: "Create User",
		},
		// {
		// 	per_id: 9,
		// 	per_name: "delete-user",
		// 	per_label: "Delete User",
		// },
		// {
		// 	per_id: 10,
		// 	per_name: "edit-user-roles",
		// 	per_label: "Edit User Roles",
		// },
		// {
		// 	per_id: 11,
		// 	per_name: "edit-user-permissions",
		// 	per_label: "Edit User Permissions",
		// },
		// {
		// 	per_id: 12,
		// 	per_name: "view-all-users",
		// 	per_label: "View All Users",
		// },
		// {
		// 	per_id: 13,
		// 	per_name: "edit-user-status",
		// 	per_label: "Edit User Status",
		// },
		{
			per_id: 14,
			per_name: "create-tag",
			per_label: "Create Tag",
		},
		{
			per_id: 15,
			per_name: "edit-tag",
			per_label: "Edit Tag",
		},
		{
			per_id: 16,
			per_name: "delete-tag",
			per_label: "Delete Tag",
		},
	]);

	const mailRegex =
		// eslint-disable-next-line unicorn/better-regex
		/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	const passwordRegex =
		/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

	const isMobileSize = computed(() => width.value < 768);
	const isMobileView = computed(
		() => isMobileSize.value && isMobileToggled.value,
	);
	const isVisibleSideBar = ref(!isMobileSize.value);

	watch(isMobileSize, (val) => {
		if (!val) {
			isMobileToggled.value = false;
		}
	});

	function decodePath(name: string) {
		return simpleDecrypt("abc", name);
	}
	function encodePath(name: string) {
		return simpleCrypt("abc", name);
	}
	/*async function onClickLoginPage() {
		// Used to return user to the previous page
		const currentPageName = String(route.name);

		// Exceptions - Auth pages
		let ppn: string | undefined = encodePath(currentPageName);
		if (route.matched.some((rt) => rt.name === "AuthPage")) {
			ppn = undefined;
		}

		// await router.push({
		// 	name: "LoginPage",
		// 	query: {
		// 		ppn,
		// 	},
		// });
	}*/

	function smoothScrollIntoView(id: string) {
		const el = document.getElementById(id);

		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	return {
		isMobileToggled,
		isMobileView,
		decodePath,
		encodePath,
		// onClickLoginPage,
		APP_NAME,
		formatDatepickerDate,
		width,
		smoothScrollIntoView,
		isMobileSize,
		mailRegex,
		passwordRegex,
		isVisibleSideBar,
		statuses,
		utcToLocale,
		getFirstLetter,
		userStatuses,
		roles,
		permissions,
		getCategories,
		categoryList,
		createCategoryVisible,
		newCategoryParent,
		geoscopeList,
		getGeoscopes,
		createGeoscopeVisible,
		getEntities,
		entityList,
		editEntityVisible,
		selectedEntity,
		createEntityVisible,
		userData,
		getUser,
		isWsConnecting,
		isAppReady,
		markAppReady,
		waitForAppReady,
		playerToken,
		mmgToken,
		isVisibleCreateRoomDialog,
		MAIN_CATEGORIES,
		SINGLE_PLAYER_GAME_TYPES,
		MULTI_PLAYER_GAME_TYPES,
		isAppLoading,
		gameToken,
		isVisibleJoinDialog,
		isRoomProtected,
		gamePlayers,
		isVisibleLobbyModal,
		getReadyState,
		isWrongPassword,
		isLoadingJoinGame,
		errorMessage,
		errorToastTrigger,
		multiplayerGameActive,
		multiplayerGamePlayerList,
		multiplayerGameMovies,
		multiplayerGameCategory,
		multiplayerGameSecondsLeft,
		multiplayerGameIsActive,
		multiplayerGameIsCorrect,
		multiplayerGameRound,
		multiplayerGameIsOver,
		multiplayerGameResults,
	};
});
